import { Environment } from "globals/enums";
import { CanvasDimensions } from "globals/interfaces";

/**
 * Maximum duration (in seconds) for test evironment video
 * @memberof EnvironmentVariable REACT_APP_TEST_ENV_MAX_DURATION
 * @default 60
 */
export const testEnvMaxDuration: number = +(process.env.REACT_APP_TEST_ENV_MAX_DURATION || 60);
/**
 * Environment for identifying Environment Name.
 * @memberof EnvironmentVariable REACT_APP_ENVIRONMENT
 */
export const environmentName: string = process.env.REACT_APP_ENVIRONMENT || Environment.Development;

/**
 * Environment for base Proview API Urls.
 * @memberof EnvironmentVariable REACT_APP_PROVIEW_API_BASE
 */
export const proctoringApiBaseUrl: string | undefined =
  process.env.REACT_APP_PROCTORING_API_BASE_URL;

// Webpack resolves this from init.js, including when embedded on a Moodle page.
// These loaders are emitted with their anonymous AMD exports removed.
export const mediapipeWasmUrl = `${__webpack_public_path__}mediapipe/wasm`;
export const faceDetectionModelUrl = `${__webpack_public_path__}mediapipe/models/face_detector.tflite`;
export const objectDetectionModelUrl = `${__webpack_public_path__}mediapipe/models/object_detector.tflite`;
export const objectDetectionPersonScoreThreshold = 0.72;
// Lower threshold for the general object detector: efficientdet_lite0 produces lower raw scores
// for non-person categories (book, cell phone, remote) than cocoSSD did, so 0.35 was chosen
// after manual validation against real proctoring footage to minimize false-negatives while
// keeping false-positive rates acceptable.
// Note: when isObjectDetectionEnabled is on, MultiplePeopleDetected is driven by this detector,
// so personDetections are re-filtered to objectDetectionPersonScoreThreshold (0.72) at runtime
// before triggering person alerts — the 0.35 floor only applies to book/phone/remote detections.
export const objectDetectionScoreThreshold = 0.35;
// Photo ID checks only. The face on an ID card is small and often a little blurred, so the webcam
// values above reject valid IDs. These only confirm that a face and a person are pictured.
export const photoIdFaceDetectionConfidence = 0.2;
export const photoIdPersonScoreThreshold = 0.2;

/**
 * Environment for base Platform Stream API Urls.
 * @memberof EnvironmentVariable REACT_APP_PLATFORM_STREAM_API_BASE
 */
export const platformStreamApiBaseUrl: string | undefined =
  process.env.REACT_APP_PLATFORM_STREAM_API_BASE;

/**
 * Environment for react app Id
 * @memberof EnvironmentVariable REACT_APP_APP_ID
 */
export const appId: string | undefined = process.env.REACT_APP_APP_ID;

/**
 * Environment for Firebase API Key.
 * @memberof EnvironmentVariable FIREBASE_API_KEY
 */
export const firebaseAPIKey: string | undefined = process.env.REACT_APP_FIREBASE_API_KEY;

/**
 * Environment for Firebase Database URL.
 * @memberof EnvironmentVariable FIREBASE_DATABASE_URL
 */
export const firebaseDatabaseURL: string | undefined = process.env.REACT_APP_FIREBASE_DATABASE_URL;

/**
 * Environment for Firebase Project ID.
 * @memberof EnvironmentVariable FIREBASE_PROJECT_ID
 */
export const firebaseProjectID: string | undefined = process.env.REACT_APP_FIREBASE_PROJECT_ID;

/**
 * Environment for Firebase APP ID.
 * @memberof EnvironmentVariable FIREBASE_APP_ID
 */
export const firebaseAPPID: string | undefined = process.env.REACT_APP_FIREBASE_APP_ID;

/**
 *  Constant for photo id max file size
 *  Default 1024 KB
 */
export const photoIdMaxFileSize = 3000;

export const getStreamAppKey: string | undefined = process.env.REACT_APP_GET_STREAM_KEY;

/**
 * Environment for Twilio audio token refresh time in milli seconds.
 * @memberof EnvironmentVariable TWILIO_AUDIO_TOKEN_REFRESH
 * Default 30000
 */
export const twilioAudioTokenRefresh: number = +(
  process.env.REACT_APP_TWILIO_AUDIO_TOKEN_REFRESH || 30000
);

/**
 * Environment for Mixpanel token.
 * @memberof EnvironmentVariable MIXPANEL_TOKEN
 */
export const mixpanelToken: string | undefined = process.env.REACT_APP_MIXPANEL_TOKEN;

/**
 * Environment for x client version.
 * @memberof EnvironmentVariable X_TALVIEW_CLIENT_VERSION
 */
export const xClientVersion: string = process.env.REACT_APP_X_TALVIEW_CLIENT_VERSION || "0.0.0";

/**
 * Environment for precheck poll interval.
 * @memberof EnvironmentVariable REACT_APP_PRECHECK_POLL_INTERVAL
 */
export const precheckPollingInterval: number = +(
  process.env.REACT_APP_PRECHECK_POLL_INTERVAL || 10000
);

/**
 * Environment for ping interval.
 * @memberof EnvironmentVariable REACT_APP_PING_INTERVAL
 */
export const pingInterval: number = +(process.env.REACT_APP_PING_INTERVAL || 10000);

/**
 *  Constant for canvas Dimensions for camera garb and screen garb
 */
export const canvasDimensions: CanvasDimensions = {
  canvasWidth: 920,
  canvasHeight: 542
};

/**
 *  Constant for canvas Dimensions for photoId and FaceCapture
 */
export const photoIdCanvasDimensions: CanvasDimensions = {
  canvasWidth: 720,
  canvasHeight: 540
};

/**
 *  Constant for chat open audio
 */
export const chatOpenAudio = "https://cdn.freesound.org/previews/320/320655_5260872-lq.mp3";

/**
 *  Constant for solution path of face detection
 */
export const solutionPath = "https://cdn.jsdelivr.net/npm/@mediapipe/face_detection";

/**
 *  Constant for offline threshold
 */
export const candidateOfflineThreshold: number = +(
  process.env.candidate_offline_threshold || 30000
);

/**
 *  Constant for alerts that should notify the candidate
 */
export const monitoringAlertsToNotifyCandidate = [214, 215, 216, 259, 227, 261, 212, 213, 260, 207];

export const monitoringAlertTranslationKeys: Record<number, string> = {
  227: "monitoring_alert.test_taker_absent",
  260: "monitoring_alert.book_detected",
  259: "monitoring_alert.mobile_detected",
  215: "monitoring_alert.additional_person",
  261: "monitoring_alert.partial_face",
  213: "monitoring_alert.suboptimal_proctoring_condition",
  214: "monitoring_alert.unauthorized_material",
  216: "monitoring_alert.screen_suspicious_behaviour",
  212: "monitoring_alert.screen_suboptimal_proctoring_condition",
};


/**
 *  Constant for alerts that should notify the candidate
 */
export const alertListToNotifyCandidate = [
  5,
  13,
  16,
  126,
  127,
  81,
  85,
  128,
  169,
  170,
  202,
  ...monitoringAlertsToNotifyCandidate
];

/**
 *  Constant for image max file size in firebase chat
 *  Default 5 MB
 */
export const chatImageMaxFileSize = 5000;

/**
 * Environment for fresh chat token.
 * @memberof EnvironmentVariable REACT_APP_FRESH_CHAT_TOKEN
 */
export const freshChatToken: string | undefined = process.env.REACT_APP_FRESH_CHAT_TOKEN;

/**
 * Environment for fresh chat host.
 * @memberof EnvironmentVariable REACT_APP_FRESH_CHAT_HOST
 */
export const freshChatHost: string | undefined = process.env.REACT_APP_FRESH_CHAT_HOST;

/**
 * Environment for fresh chat site_id.
 * @memberof EnvironmentVariable REACT_APP_FRESH_CHAT_SITE_ID
 */
export const freshChatSiteId: string | undefined = process.env.REACT_APP_FRESH_CHAT_SITE_ID;

/**
 * Environment for fresh chat script.
 * @memberof EnvironmentVariable REACT_APP_FRESH_CHAT_SCRIPT
 */
export const freshChatScript: string | undefined = process.env.REACT_APP_FRESH_CHAT_SCRIPT;

/**
 *  Constant for DEFAULT_NOTIFICATION_THRESHOLD
 *  Default 2 times
 */
export const defaultNotificationThreshold = 2;

/**
 *  Constant for trace sample rate of sentry
 */
export const tracesSampleRate = 1.0;

/**
 *  Constant for session replay sample rate of sentry
 */
export const replaysSessionSampleRate = 1.0;

/**
 *  Constant for error replay sample rate of sentry
 */
export const replaysOnErrorSampleRate = 1.0;

/**
 *  Constant for sample rate of sentry
 */
export const sampleRate: number = +(process.env.REACT_APP_SAMPLE_RATE || 0.2);

/**
 *  Constant for default speed test url
 */
export const speedTestUrl: string =
  process.env.REACT_APP_SPEED_TEST_URL || "https://speed.talview.com";

/**
 *  Constant for Twilio diagnostics url
 */
export const twilioDiagnosticsUrl: string =
  process.env.REACT_APP_TWILIO_TEST_URL || "https://video-diagnostics.talview.org/?provider=twilio";

/**
 *  Constant for Livekit diagnostics url
 */
export const livekitDiagnosticsUrl: string =
  process.env.REACT_APP_LIVEKIT_TEST_URL || "https://video-diagnostics.talview.org";

export const secondaryDeviceAppUrl: string | undefined =
  process.env.REACT_APP_SECONDARY_DEVICE_APP_URL;

/**
 *  Constant for the ATX (v8) SDK bundle url, loaded when the backend hands a
 *  session over (see src/switcher).
 *
 *  Hardcoded rather than read from the environment: one CDN path serves every
 *  environment, and the backend's `dsn` already decides whether it loads at
 *  all. Note this leaves no client-side kill switch — the backend is the only
 *  way to stop a handoff.
 */
export const atxSdkUrl = "https://sdk.tlv.cx/session/init.js";

export const allowedStartBufferTime = 30;

/**
 *  Constant for secure browser launch url
 */
export const sbLaunchUrl = "https://pages.talview.com/securebrowser/index.html";

/**
 *  Constant for default terms and conditions url
 */
export const defaultTermsUrl = "https://www.talview.com/terms";

/**
 *  Constant for default privacy url
 */
export const defaultPrivacyUrl = "https://www.talview.com/privacy";

/**
 *  Constant for default support url
 */
export const defaultSupportUrl = "https://certification.talview.com/support/tickets/new";

/**
 *  Constant for default support email
 */
export const defaultSupportEmail = "dpo@talview.com";

/**
 *  Constant for max connection retries for streaming
 */
export const maxConnectionRetries = 3;

/**
 *  Constant for stream reconnection timeout
 */
export const retryTimeoutInMS = 10000;

export const livekitWSUrl = process.env.REACT_APP_LIVEKIT_WSURL;

export const defaultColor = "#1cb841";

export const defaultRestrictedURLDomains =
  "google.com, mozilla.org, microsoft.com, apple.com, opera.com, brave.com, torproject.org, slack.com, zoom.us, skype.com, web.whatsapp.com, discord.com, telegram.org, dropbox.com, openai.com, github.com/copilot, jasper.ai, writesonic.com, copy.ai, quillbot.com, wordtune.com, hemingwayapp.com, deepl.com, chegg.com, coursehero.com, brainly.com, scribd.com, studocu.com, sparknotes.com, cliffsnotes.com, quizlet.com, bartleby.com, answers.com, slader.com, symbolab.com, numerade.com, mathway.com, wolframalpha.com, tutor.com, studypool.com, papersowl.com, drive.google.com, onedrive.live.com, box.com, weTransfer.com, mega.nz, mediafire.com, youtube.com, tiktok.com, facebook.com, instagram.com, reddit.com, twitter.com, x.com, twitch.tv, snapchat.com, whatsapp.com, loom.com, screenapp.io, veed.io, panopto.com, recordcast.com, screenity.io, fluvid.com, clipchamp.com, anydesk.com, www.teamviewer.com, www.mozilla.org/firefox, www.google.com/chrome, www.opera.com, www.apple.com/safari, www.microsoft.com/edge, www.webex.com, riot.im, www.seamonkey-project.org, www.microsoft.com/en-us/microsoft-teams/download-app, www.goto.com/meeting, element.io, slack.com/downloads/windows, www.techsmith.com/video-editor.html, www.techsmith.com, www.skype.com/en/get-skype, www.join.me, rpcsuite.com, www.remotepc.com, www.beamyourscreen.com, www.aeroadmin.com, www.realvnc.com, www.ammyy.com, www.grammarly.com, www.linkedin.com, brave.com/download, slack.com/downloads/mac, www.grammarly.com/native/mac";

export const defaultBlackListedSoftwaresWindows =
  "obs64.exe, CamtasiaStudio.exe, Loom.exe, ScreenPalLauncher.exe, Snagit32.exe, ShareX.exe, bdcam.exe, ScreenRecorder.exe, GameBar.exe, ScreenRec.exe, FreeCam.exe, ApowerREC.exe, Filmora.exe, hypercam.exe, simplescreenrecorder, vlc.exe, anydesk, teamviewer.exe, webex, ciscocollabhost, riot, seamonkey, teams.exe, g2m, telegram.exe, zoom.exe, element, slack.exe, camtasia, camrecorder, camplay, discord, skype.exe, join.me, rpcsuite, beamyourscreen, mikogo, aa_v,ammy, firefox, opera.exe, safari.exe, msedge.exe, RelayRecorder, msedge.exe, chrome.exe, DeepL, Reverso, iTranslate, Translator.WPF, crow, od_windows, wuying, Whatsapp.Root.exe";

export const defaultBlackListedSoftwaresMac =
  "OBS, Camtasia 2024, Loom, ScreenPal, Snagit 2024, Movavi Screen Recorder, CleanShot X, QuickTime Player, ScreenRec, ApowerREC, Wondershare Filmora, VLC, AnyDesk, anydesk, TeamViewer, TeamViewer_Service, firefox, Google Chrome, Google Chrome Helper, Opera, Safari, Teams, Slack, Skype, FaceTime, Brave Browser, Brave Browser Helper, zoom.us, ZoomOpener, Grammarly, Grammarly Editor, Camtasia, screenrecorder, TechSmith Capture, NeuraQuizRumble, 网易有道翻译, Runner, BengaliDictionary, Boighor, HelloTalk_Binary, YoudaoDictPro, LDOCE, macmillan, ThesaurusFree, Power Thesaurus, Woordle, Dictionary, MagooshQuizApp, eurodict, ABBYY FineReader PDF, Mate Translate, DeepL, Reverso, Google Translate, Microsoft Translator, iTranslatev2, Translate, TranslatorJoshua, Linguee, Translator, PROMT Master NMT UNI, NaverSearch, 百度, 网易有道翻译, 百度, Runner, merriam, Whatsapp.Root.exe";

export const orgIdsWithFaceDetection = process.env.REACT_APP_ORG_IDS_WITH_FACE_DETECTION || "";

/**
 * Comma-separated list of organization IDs that should use image rescaling
 */
export const orgIdsWithRescaleImage = process.env.REACT_APP_ORG_IDS_WITH_RESCALE_IMAGE || "";

export const waitTimeForRecordingStart = 60000;

// --- Twilio Video Constraint Iterations ---
export interface VideoConstraintIteration {
  maxVideoBitrate: number;
  camera: { width: number; height: number; frameRate: number };
  screen: { width: number; height: number; frameRate: number };
}

export const VIDEO_CONSTRAINT_ITERATIONS: Record<number, VideoConstraintIteration> = {
  1: {
    maxVideoBitrate: 200000,
    camera: { width: 360, height: 240, frameRate: 2 },
    screen: { width: 640, height: 480, frameRate: 2 }
  },
  2: {
    maxVideoBitrate: 200000,
    camera: { width: 360, height: 240, frameRate: 5 },
    screen: { width: 640, height: 480, frameRate: 5 }
  },
  3: {
    maxVideoBitrate: 200000,
    camera: { width: 640, height: 480, frameRate: 2 },
    screen: { width: 1280, height: 720, frameRate: 2 }
  },
  4: {
    maxVideoBitrate: 200000,
    camera: { width: 640, height: 480, frameRate: 5 },
    screen: { width: 1280, height: 720, frameRate: 5 }
  }
};

/**
 * Returns the Twilio video constraint config for an org, if enabled by env var.
 * @param orgId Organization ID
 */
export function getTwilioVideoConstraintConfig(
  orgId: string
): VideoConstraintIteration | undefined {
  if (!orgId) return undefined;

  // Parse the environment variable to get org-iteration mappings
  const orgIterationMappings = (process.env.REACT_APP_SCREEN_CONSTRAINT_ENABLED_ORG || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  // Find the mapping for the current org
  const orgMapping = orgIterationMappings.find((mapping) => {
    const [mappedOrgId] = mapping.split("@");
    return mappedOrgId === orgId;
  });

  // If no mapping found for this org, return undefined (no constraints)
  if (!orgMapping) return undefined;

  // Extract the iteration from the mapping (format: "orgId@iteration")
  const [, iterationStr] = orgMapping.split("@");
  const iteration = parseInt(iterationStr || "1", 10);

  // Return the corresponding iteration config or default to iteration 1
  return VIDEO_CONSTRAINT_ITERATIONS[iteration] || VIDEO_CONSTRAINT_ITERATIONS[1];
}
